import { useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WelcomeMessage from "./WelcomeMessage";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import ChatInput from "./ChatInput";
import { sendMessage } from "../services/api";

export default function ChatWindow({ userId, sessionId }) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = useCallback(
    async (text) => {
      if (!sessionId) {
        setError("Session not ready. Please refresh the page.");
        return;
      }

      setError(null);
      const userMsg = { role: "user", content: text };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const data = await sendMessage(userId, sessionId, text);
        const botMsg = {
          role: "assistant",
          content: data.response,
          sources: data.sources || [],
          messageId: data.gapId,
          wasAnswered: data.wasAnswered,
        };
        setMessages((prev) => [...prev, botMsg]);
      } catch (err) {
        setError("Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [userId, sessionId]
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        maxWidth: 720,
        width: "100%",
        mx: "auto",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 3 }}>
        <WelcomeMessage onSuggestionClick={handleSend} />
        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}
        {isLoading && <TypingIndicator />}
        {error && (
          <Typography color="error" variant="body2" sx={{ px: 1, mb: 1, fontSize: 13 }}>
            {error}
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>
      <ChatInput onSend={handleSend} disabled={isLoading} />
    </Box>
  );
}
